import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Select, message, Spin } from 'antd';
import { ShopOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { fetchSucursales, selectSucursal } from '../../redux/sucursal/actionCreator';

const { Option } = Select;

const SucursalGuard = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.login?.user);
  const currentSucursal = useSelector(state => state.sucursal?.current);
  const sucursalesList = useSelector(state => state.sucursal?.list || []);
  const sucursalesLoading = useSelector(state => state.sucursal?.loading);

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSucursalId, setSelectedSucursalId] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const checkSucursal = async () => {
      // Solo verificar una vez
      if (checked) return;

      // Si no hay usuario, no hacer nada
      if (!user) {
        setChecked(true);
        return;
      }

      // Si el usuario tiene sucursal asignada
      if (user.sucursalAsignada) {
        // Si no hay sucursal en el state, cargarla
        if (!currentSucursal) {
          try {
            const sucursales = await dispatch(fetchSucursales());
            const assignedId =
              typeof user.sucursalAsignada === 'object'
                ? user.sucursalAsignada.id
                : user.sucursalAsignada;
            const sucursalAsignada = sucursales.find(s => s.id === assignedId);
            if (sucursalAsignada) {
              dispatch(selectSucursal(sucursalAsignada));
            }
          } catch (error) {
            console.error('Error al cargar sucursal asignada:', error);
            message.error('Error al cargar la sucursal asignada');
          }
        }
        setChecked(true);
        return;
      }

      // Si es admin sin sucursal asignada
      if (user.role === 'admin') {
        // Si ya hay una sucursal seleccionada (desde localStorage), no hacer nada
        if (currentSucursal) {
          setChecked(true);
          return;
        }
        // Si no hay sucursal seleccionada, cargar sucursales y mostrar modal
        try {
          await dispatch(fetchSucursales());
          setModalVisible(true);
        } catch (error) {
          message.error('Error al cargar sucursales');
        }
        setChecked(true);
        return;
      }

      // Otros usuarios
      setChecked(true);
    };

    checkSucursal();
  }, [user, currentSucursal, dispatch, checked]);

  const handleSucursalSelect = () => {
    if (!selectedSucursalId) {
      message.warning('Por favor selecciona una sucursal');
      return;
    }

    const sucursal = sucursalesList.find(s => s.id === selectedSucursalId);
    if (sucursal) {
      dispatch(selectSucursal(sucursal));
      message.success(`Sucursal ${sucursal.nombre} seleccionada`);
      setModalVisible(false);
    }
  };

  // Si es admin sin sucursal asignada y no hay sucursal seleccionada, no mostrar contenido
  const shouldBlockContent = user?.role === 'admin' && !user?.sucursalAsignada && !currentSucursal;

  return (
    <>
      {!shouldBlockContent && children}

      {/* Modal de Selección de Sucursal */}
      <Modal
        title={
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <ShopOutlined style={{ fontSize: '48px', color: '#1890ff', display: 'block', marginBottom: '16px' }} />
            <h2 style={{ margin: 0, fontSize: '24px' }}>¡Bienvenido {user?.name}!</h2>
            <p style={{ color: '#666', fontSize: '16px', marginTop: '8px' }}>
              Selecciona la sucursal con la que deseas trabajar
            </p>
          </div>
        }
        visible={modalVisible}
        onOk={handleSucursalSelect}
        onCancel={() => {
          message.warning('Debes seleccionar una sucursal para continuar');
        }}
        okText="Continuar"
        cancelText="Cancelar"
        width={500}
        centered
        closable={false}
        maskClosable={false}
        okButtonProps={{
          size: 'large',
          disabled: !selectedSucursalId,
        }}
        cancelButtonProps={{
          size: 'large',
          style: { display: 'none' },
        }}
      >
        {sucursalesLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" tip="Cargando sucursales..." />
          </div>
        ) : (
          <div style={{ padding: '20px 0' }}>
            <Select
              style={{ width: '100%' }}
              placeholder="Selecciona una sucursal"
              onChange={setSelectedSucursalId}
              value={selectedSucursalId}
              size="large"
              showSearch
              autoFocus
              filterOption={(input, option) => {
                const { children: optionChildren } = option;
                if (typeof optionChildren === 'string') {
                  return optionChildren.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }
                return false;
              }}
            >
              {sucursalesList.map(sucursal => (
                <Option key={sucursal.id} value={sucursal.id}>
                  {sucursal.nombre} ({sucursal.codigo})
                </Option>
              ))}
            </Select>

            {sucursalesList.length === 0 && (
              <p style={{ marginTop: '16px', color: '#999', textAlign: 'center' }}>No hay sucursales disponibles</p>
            )}

            {selectedSucursalId && (
              <div
                style={{
                  marginTop: '24px',
                  padding: '16px',
                  background: '#f0f7ff',
                  borderRadius: '8px',
                  border: '1px solid #91d5ff',
                }}
              >
                <p style={{ margin: 0, color: '#0050b3', fontWeight: 500 }}>
                  ✓ Sucursal seleccionada: {sucursalesList.find(s => s.id === selectedSucursalId)?.nombre}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

SucursalGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SucursalGuard;
